import { useAllVoices } from '@/hooks/useAllVoices';
import PlugCircle from '@/images/Plug-Circle.svg';
import { ttsVoiceProviderFilter } from '@/state/presenters';
import { Providers } from '@d-id/types';
import { SelectChangeEvent } from '@mui/material/Select';
import { useMemo } from 'react';
import { useRecoilState } from 'recoil';
import { FilterChipMultiSelect, FilterOption } from './FilterChipMultiSelect';

const providerDisplayNames: Record<string, string> = {
    [Providers.Elevenlabs]: 'ElevenLabs',
    [Providers.Microsoft]: 'Microsoft',
    [Providers.Google]: 'Google',
    [Providers.AzureOpenAi]: 'OpenAI',
};

const getProviderDisplayName = (provider: Providers): string => {
    return providerDisplayNames[provider] || provider;
};

const providerOrder: Providers[] = [Providers.Elevenlabs, Providers.Microsoft, Providers.Google, Providers.AzureOpenAi];

const getProviderOrder = (provider: Providers): number => {
    const index = providerOrder.indexOf(provider);
    return index === -1 ? 999 : index;
};

interface Props {
    localProviders?: Providers[];
}

export const ProviderChipMultiSelect = ({ localProviders }: Props) => {
    const { data = [] } = useAllVoices();
    const [ttsVoiceProviderFilterValue, setTtsVoiceProviderFilter] = useRecoilState(ttsVoiceProviderFilter);

    const handleChange = (e: SelectChangeEvent<any>) => {
        setTtsVoiceProviderFilter(e.target.value);
    };

    // Dynamically create provider options from available voices
    // If localProviders is provided, only show those providers
    const availableProviders = useMemo(() => {
        const providersSet = new Set<Providers>(data.map(voice => voice.provider));
        let filteredProviders = Array.from(providersSet);

        if (!!localProviders?.length) {
            const localProvidersSet = new Set(localProviders);
            filteredProviders = filteredProviders.filter(provider => localProvidersSet.has(provider));
        }

        return filteredProviders
            .map(provider => ({
                value: provider,
                label: getProviderDisplayName(provider),
            }))
            .sort((a, b) => getProviderOrder(a.value) - getProviderOrder(b.value));
    }, [data, localProviders]);

    const values = ttsVoiceProviderFilterValue.map(v => availableProviders.find(p => p.value === v)).filter(v => !!v) as FilterOption[];

    return (
        <>
            <FilterChipMultiSelect
                data-testid="voice-provider-filter"
                id="provider-filter"
                values={values}
                options={availableProviders}
                onChange={handleChange}
                valueToRender={{ label: 'Provider', icon: PlugCircle }}
            />
        </>
    );
};
